import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';

// Add responsive styles
const styles = `
  @media (max-width: 768px) {
    .hero-title {
      font-size: 2rem !important;
    }
    .hero-description {
      font-size: 16px !important;
    }
  }
`;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, isLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    twoFactorCode: ''
  });
  
  const [is2FA, setIs2FA] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const validateEmail = (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear specific error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleLogin = async () => {
    setErrors({});

    // Validate email
    if (!validateEmail(formData.email)) {
      setErrors({ email: 'Please enter a valid email address.' });
      return;
    }

    if (!formData.email || !formData.password) {
      setErrors({ general: 'Please fill in all fields.' });
      return;
    }

    setIsSubmitting(true);

    try {
      await login(formData.email, formData.password);
      toast.success('Login successful! Redirecting...');
      navigate('/');
    } catch (error) {
      console.log("Login error:", error);
      
      if (error.response?.status === 400) {
        setErrors({ general: error.response.data.message });
      } else if (error.response?.status === 401) {
        setErrors({ general: 'Invalid email or password.' });
      } else if (error.response?.status === 422) {
        // Handle 2FA requirement
        if (error.response.data.message?.includes('2FA')) {
          setIs2FA(true);
          setErrors({ general: 'Please enter your 2FA code.' });
        } else {
          setErrors({ general: 'Please check your input and try again.' });
        }
      } else {
        setErrors({ general: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FASubmit = async () => {
    if (!formData.twoFactorCode) {
      setErrors({ general: 'Please enter the 2FA code.' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Note: You'll need to implement 2FA verification in your auth context
      // await verify2FA(formData.twoFactorCode);
      toast.success('2FA verification successful!');
      navigate('/');
    } catch (error) {
      setErrors({ general: error.message || 'Invalid 2FA code.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle Enter key for form submission
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (is2FA) {
        handle2FASubmit();
      } else {
        handleLogin();
      }
    }
  };

  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          fontFamily: 'Inter, sans-serif',
          backgroundColor: '#FEFDF6',
        }}>
          <div>Checking authentication...</div>
        </div>
      </>
    );
  }

  if (isAuthenticated) {
    return (
      <>
        <style>{styles}</style>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '100vh',
          fontFamily: 'Inter, sans-serif',
          backgroundColor: '#FEFDF6',
        }}>
          <div>Redirecting to dashboard...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div style={{ 
        minHeight: '100vh',
        fontFamily: 'Inter, sans-serif',
        backgroundColor: '#FEFDF6',
        width: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box',
      }}>
        {/* Hero Section */}
        <div className="hero-section" style={{
          position: 'relative',
          minHeight: '100vh',
          paddingTop: '40px',
          paddingBottom: '40px',
          overflow: 'hidden',
          background: 'linear-gradient(to bottom right, #74C0E6, #FEFDF6, rgba(189, 182, 253, 0.3))',
          width: '100%',
          boxSizing: 'border-box',
        }}>
          {/* Decorative background elements */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            zIndex: 0,
          }}>
            <div style={{
              position: 'absolute',
              top: '80px',
              left: '40px',
              width: '256px',
              height: '256px',
              backgroundColor: 'rgba(32, 150, 243, 0.2)',
              borderRadius: '50%',
              filter: 'blur(60px)',
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '80px',
              right: '40px',
              width: '384px',
              height: '384px',
              backgroundColor: 'rgba(108, 204, 22, 0.2)',
              borderRadius: '50%',
              filter: 'blur(60px)',
            }}></div>
          </div>

          {/* Hero Content */}
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 16px',
            textAlign: 'center',
            position: 'relative',
            zIndex: 10,
          }}>
            {/* Logo */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
            }}>
              <div className="logo-group" style={{
                position: 'relative',
                backgroundColor: '#6CCC16',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '8px',
                borderBottomLeftRadius: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                cursor: 'pointer',
              }}>
                <div style={{
                  overflow: 'hidden',
                  transition: 'all 0.5s ease-in-out',
                  maxWidth: '46px',
                }} className="logo-text-container">
                  <span style={{
                    fontWeight: 900,
                    fontSize: '24px',
                    letterSpacing: '-0.5px',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}>IMObituary</span>
                </div>
                <div style={{
                  position: 'relative',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'visible',
                  width: '24px',
                  height: '24px',
                }}>
                  {/* Mail truck */}
                  <div className="mail-truck" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 10,
                    opacity: 0,
                  }}>
                    <i className="fa-solid fa-truck" style={{ fontSize: '12px', color: 'white' }}></i>
                  </div>
                  {/* Envelope icons */}
                  <i className="fa-regular fa-envelope envelope-closed-animate" style={{
                    fontSize: '18px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 1,
                  }}></i>
                  <i className="fa-regular fa-envelope-open envelope-open-animate" style={{
                    fontSize: '18px',
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 20,
                    opacity: 0,
                  }}></i>
                  {/* Dollar signs */}
                  <span className="dollar-sign-1" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-2" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                  <span className="dollar-sign-3" style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 30,
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    opacity: 0,
                  }}>$</span>
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '-8px',
                  left: 0,
                  width: 0,
                  height: 0,
                  borderTop: '8px solid #6CCC16',
                  borderRight: '8px solid transparent',
                }}></div>
              </div>
            </div>

            <h1 className="hero-title" style={{
              fontSize: '3rem',
              fontWeight: 900,
              color: '#000000',
              marginBottom: '16px',
              letterSpacing: '-1px',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
            }}>
              Log In
            </h1>
            <p className="hero-description" style={{
              fontSize: '20px',
              color: '#206884',
              maxWidth: '600px',
              margin: '0 auto 48px',
              fontWeight: 600,
            }}>
              Welcome back! Sign in to your account to continue.
            </p>

            {/* Form Card */}
            <div style={{ 
              maxWidth: '500px',
              width: '100%',
              margin: '0 auto',
              boxSizing: 'border-box',
            }}>
              <div style={{ 
                backgroundColor: 'white',
                borderRadius: '16px',
                padding: '40px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
                width: '100%',
                boxSizing: 'border-box',
                textAlign: 'left',
              }}>
                <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }} id="loginform">
                  {/* Error Alert */}
                  {errors.general && (
                    <div style={{ 
                      backgroundColor: '#f8d7da', 
                      color: '#721c24', 
                      padding: '12px', 
                      borderRadius: '8px', 
                      marginBottom: '20px',
                      border: '1px solid #f5c6cb'
                    }}>
                      {errors.general}
                    </div>
                  )}

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      color: '#333', 
                      fontWeight: '500',
                      display: 'block',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      Username or Email
                    </label>
                    <input 
                      type="email" 
                      name="email" 
                      placeholder="Example@example.com" 
                      value={formData.email}
                      onChange={handleInputChange('email')}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting}
                      style={{ 
                        border: '1px solid #ddd', 
                        padding: '10px 15px',
                        width: '100%',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }} 
                      required 
                    />
                    {errors.email && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.email}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ 
                      color: '#333', 
                      fontWeight: '500',
                      display: 'block',
                      marginBottom: '8px',
                      textAlign: 'left'
                    }}>
                      Password
                    </label>
                    <input 
                      type="password" 
                      name="password" 
                      placeholder="Enter your password" 
                      value={formData.password}
                      onChange={handleInputChange('password')}
                      onKeyPress={handleKeyPress}
                      disabled={isSubmitting}
                      style={{ 
                        border: '1px solid #ddd', 
                        padding: '10px 15px',
                        width: '100%',
                        borderRadius: '8px',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }} 
                      required 
                    />
                    {errors.password && (
                      <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>
                        {errors.password}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    onClick={handleLogin}
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      backgroundColor: '#206884',
                      color: 'white',
                      border: 'none',
                      padding: '12px',
                      borderRadius: '8px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      opacity: isSubmitting ? 0.7 : 1,
                      marginBottom: '20px',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting) {
                        e.target.style.backgroundColor = '#1a5568';
                        e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting) {
                        e.target.style.backgroundColor = '#206884';
                        e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                      }
                    }}
                  >
                    {isSubmitting ? 'Signing In...' : 'Sign In'}
                  </button>

                  {/* 2FA Code Field */}
                  {is2FA && (
                    <div style={{ marginTop: '20px' }}>
                      <label style={{ 
                        color: '#333', 
                        fontWeight: '500',
                        display: 'block',
                        marginBottom: '8px',
                        textAlign: 'left'
                      }}>
                        Two-Factor Authentication Code
                      </label>
                      <input 
                        type="text" 
                        placeholder="Enter 2FA code" 
                        value={formData.twoFactorCode}
                        onChange={handleInputChange('twoFactorCode')}
                        onKeyPress={handleKeyPress}
                        disabled={isSubmitting}
                        style={{ 
                          border: '1px solid #ddd', 
                          padding: '10px 15px',
                          width: '100%',
                          borderRadius: '8px',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          marginBottom: '15px'
                        }} 
                      />
                      <button
                        type="button"
                        onClick={handle2FASubmit}
                        disabled={isSubmitting}
                        style={{
                          width: '100%',
                          backgroundColor: '#206884',
                          color: 'white',
                          border: 'none',
                          padding: '12px',
                          borderRadius: '8px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: isSubmitting ? 'not-allowed' : 'pointer',
                          opacity: isSubmitting ? 0.7 : 1,
                          transition: 'all 0.2s ease',
                          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.backgroundColor = '#1a5568';
                            e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSubmitting) {
                            e.target.style.backgroundColor = '#206884';
                            e.target.style.boxShadow = '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
                          }
                        }}
                      >
                        {isSubmitting ? 'Verifying...' : 'Verify 2FA'}
                      </button>
                    </div>
                  )}

                  {/* Links */}
                  <div style={{ 
                    marginTop: '30px', 
                    textAlign: 'center'
                  }}>
                    <a 
                      href="/register" 
                      style={{ 
                        color: '#206884', 
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                        display: 'block',
                        marginBottom: '8px',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                        e.target.style.color = '#1a5568';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                        e.target.style.color = '#206884';
                      }}
                    >
                      Don't have an account? Sign up
                    </a>
                    <a 
                      href="/forgot" 
                      style={{ 
                        color: '#206884', 
                        textDecoration: 'none',
                        fontSize: '14px',
                        fontWeight: '500',
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = 'underline';
                        e.target.style.color = '#1a5568';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = 'none';
                        e.target.style.color = '#206884';
                      }}
                    >
                      Forgot your password?
                    </a>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
